import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, UtensilsCrossed, Gift, Coffee, QrCode } from 'lucide-react';
import useStore from '@/store/useStore';
import { apiGetWallet, apiGetTransactions, apiGetInvoiceInfo, apiRedeem, apiScanInvoice } from '@/services/api';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import InvoiceScannerModal from '@/components/guest/InvoiceScannerModal';
import { getSelectedMerchantId, storeRoute } from '@/lib/routes';

const TX_ICONS = { earn: [UtensilsCrossed, Gift, Coffee], redeem: [Gift] };

function parseInvoiceCode(rawValue) {
  const value = (rawValue || '').trim();
  if (!value) return '';
  const decodeSafe = (input) => {
    try {
      return decodeURIComponent(input || '');
    } catch {
      return input || '';
    }
  };
  try {
    const parsed = new URL(value);
    const urlMatch = (parsed.pathname || '').match(/\/invoice\/([^/?#]+)/i);
    if (urlMatch?.[1]) return decodeSafe(urlMatch[1]).trim().toUpperCase();
  } catch {}
  const pathMatch = value.match(/\/invoice\/([^/?#]+)/i);
  if (pathMatch?.[1]) return decodeSafe(pathMatch[1]).trim().toUpperCase();
  const codeMatch = value.match(/\bINV-[A-Z0-9-]+\b/i);
  if (codeMatch?.[0]) return codeMatch[0].toUpperCase();
  return '';
}

export default function WalletPage() {
  const navigate = useNavigate();
  const {
    guestUser,
    wallet,
    transactions,
    merchantInfo,
    setWallet,
    setTransactions,
    openRedeemModal,
    openEarnModal,
    lastViewedTxId,
    setLastViewedTxId,
  } = useStore();

  const [showNotifications, setShowNotifications] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanError, setScanError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const activeMerchantId = getSelectedMerchantId();

  const fetchData = useCallback(async () => {
    try {
      const [w, t] = await Promise.all([apiGetWallet(), apiGetTransactions()]);
      setWallet(w);
      setTransactions(t);
    } catch (e) {
      console.error(e);
    }
  }, [setWallet, setTransactions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!transactions || transactions.length === 0) {
      setUnreadCount(0);
      return;
    }

    const latestId = transactions[0].id;

    // If we've never viewed any, all are unread (up to 9)
    if (!lastViewedTxId) {
      setUnreadCount(Math.min(transactions.length, 9));
      return;
    }

    // If the latest is what we last viewed, no new notifications
    if (latestId === lastViewedTxId) {
      setUnreadCount(0);
      return;
    }

    // Find how many are new since lastViewedTxId
    const index = transactions.findIndex(tx => tx.id === lastViewedTxId);
    if (index === -1) {
      // Last viewed is not in the recent list, show max
      setUnreadCount(9);
    } else {
      setUnreadCount(Math.min(index, 9));
    }
  }, [transactions, lastViewedTxId]);

  const notificationItems = useMemo(
    () => (transactions || []).slice(0, 8),
    [transactions]
  );

  const handleRedeem = () => {
    openRedeemModal({ points: wallet?.points || 0 });
  };

  const handleScanInvoice = useCallback(async (rawCode, meta = {}) => {
    const invoiceCode = parseInvoiceCode(rawCode);
    if (!invoiceCode) {
      setScanError('This QR is not a valid invoice QR. Please scan the invoice bill QR.');
      setShowScanner(false);
      return;
    }

    const promoCode = String(meta?.promoCode || '').trim().toUpperCase();

    try {
      const invoice = await apiGetInvoiceInfo(invoiceCode);
      const invoiceAmount = Number(invoice?.amount || 0);

      if (promoCode) {
        if (!Number.isFinite(invoiceAmount) || invoiceAmount <= 0) {
          setScanError('This invoice amount is invalid. Promo code cannot be applied.');
          setShowScanner(false);
          return;
        }
        await apiRedeem(0, 'source', invoiceAmount, promoCode);
      }

      const result = await apiScanInvoice(invoiceCode);
      if (promoCode) {
        result.promo_code_applied = promoCode;
        result.bill_amount = invoiceAmount;
      }
      openEarnModal(result);
      setShowScanner(false);
      await fetchData();
    } catch (err) {
      console.error('Scan failed:', err);
      const detail = err.response?.data?.detail || 'This invoice code is invalid or already used.';
      setScanError(detail);
      setShowScanner(false);
    }
  }, [fetchData, openEarnModal]);

  const levelProgress = wallet?.level_progress;
  const levelTarget = Number(levelProgress?.target || 0);
  const levelCurrent = Number(levelProgress?.current || 0);
  const levelPct = levelTarget > 0 ? Math.min((levelCurrent / levelTarget) * 100, 100) : 0;

  return (
    <div className="px-4 pt-6 pb-6 space-y-5" data-testid="wallet-page">
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-brand" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
          {merchantInfo?.restaurant_name || 'FlyBack'}
        </h2>
        <div className="flex items-center gap-3">
          <button
            className="relative"
            data-testid="notifications-btn"
            onClick={() => {
              setShowNotifications(true);
              if (transactions?.[0]?.id) {
                setLastViewedTxId(transactions[0].id);
              }
            }}
          >
            <Bell className="w-5 h-5 text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 text-[10px] rounded-full gradient-brand text-white flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-sm font-bold"
            onClick={() => navigate(activeMerchantId ? storeRoute(activeMerchantId, 'profile') : '/store')}
            data-testid="wallet-profile-btn"
          >
            {(guestUser?.name || 'G')[0]}
          </button>
        </div>
      </div>

      {/* Welcome */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-xs tracking-widest uppercase font-bold text-muted-foreground">
          Welcome back, {guestUser?.name?.split('_')[0] || 'Guest'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Active Points: <span className="font-semibold text-foreground">{(wallet?.points || 0).toLocaleString()}</span>
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
          Your Appetite,<br />Rewarded.
        </h1>
        <div className="mt-3 rounded-xl border border-border bg-card p-3 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <p className="font-semibold text-foreground">Lifetime Earned: {(wallet?.total_earned_points || 0).toLocaleString()} pts</p>
            <p className="text-brand font-semibold">{levelProgress?.label || 'Level 1'}</p>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div className="h-full rounded-full gradient-brand" style={{ width: `${levelPct}%` }} />
          </div>
          <p className="text-[11px] text-muted-foreground">{levelProgress?.display || '0 / 500 (Level 1)'}</p>
        </div>
      </motion.div>

      {/* Points Card */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-2xl p-6 text-white relative overflow-hidden gradient-brand"
        data-testid="points-card"
      >
        <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/10 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/2" />
        <div className="relative z-10">
          <p className="text-white/70 text-xs tracking-widest uppercase font-bold">Current Balance</p>
          <p className="text-5xl font-black tracking-tighter mt-1" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {wallet?.points?.toLocaleString() || '---'}
          </p>
          <p className="text-white/90 text-sm font-semibold -mt-1">FlyBack Points</p>
          <div className="flex items-center gap-1.5 mt-2 bg-white/15 rounded-full px-3 py-1 w-fit backdrop-blur-sm">
            <Gift className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Equivalent to ${wallet?.dollar_value?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              data-testid="redeem-now-btn"
              onClick={handleRedeem}
              className="w-full py-3 bg-white text-brand font-bold rounded-xl text-sm hover:bg-white/95 transition-colors"
            >
              Redeem Now
            </button>
            <button
              data-testid="scan-invoice-btn"
              onClick={() => setShowScanner(true)}
              className="w-full py-3 bg-white/20 border border-white/30 text-white font-bold rounded-xl text-sm hover:bg-white/25 transition-colors flex items-center justify-center gap-2"
            >
              <QrCode className="w-4 h-4" /> Scan QR
            </button>
          </div>
        </div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>Recent Activity</h2>
          <button
            onClick={() => navigate(activeMerchantId ? storeRoute(activeMerchantId, 'history') : '/store')}
            className="text-sm font-semibold text-brand"
            data-testid="view-all-btn"
          >
            View All
          </button>
        </div>
        <div className="space-y-2">
          {(transactions || []).slice(0, 4).map((tx, i) => {
            const IconComp = tx.type === 'earn' ? (TX_ICONS.earn[i % TX_ICONS.earn.length]) : TX_ICONS.redeem[0];
            const isEarn = tx.type === 'earn';
            return (
              <motion.div
                key={tx.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.05 }}
                className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border"
                data-testid={`transaction-${tx.id}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isEarn ? 'bg-brand-soft' : 'bg-stone-100 dark:bg-stone-800'}`}>
                  <IconComp className={`w-4 h-4 ${isEarn ? 'text-brand' : 'text-stone-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{tx.title}</p>
                  <p className="text-xs text-muted-foreground">{tx.detail}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-sm font-bold ${isEarn ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                    {isEarn ? '+' : ''}{tx.points} pts
                  </span>
                  <p className="text-[10px] text-muted-foreground">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}</p>
                </div>
              </motion.div>
            );
          })}
          {(!transactions || transactions.length === 0) && (
            <p className="text-center text-sm text-muted-foreground py-8">No activity yet. Start earning points!</p>
          )}
        </div>
      </motion.div>

      <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="notifications-modal">
          <DialogTitle className="text-lg font-bold">Notifications</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Recent points, redemption, and visit updates.
          </DialogDescription>
          <div className="space-y-2 max-h-80 overflow-auto pr-1">
            {notificationItems.length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center">No notifications yet.</p>
            )}
            {notificationItems.map((tx) => (
              <div key={tx.id} className="rounded-xl border border-border p-3">
                <p className="text-sm font-semibold">{tx.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{tx.detail}</p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {tx.points > 0 ? '+' : ''}{tx.points} pts • {tx.created_at ? new Date(tx.created_at).toLocaleString() : ''}
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <InvoiceScannerModal
        open={showScanner}
        onClose={setShowScanner}
        onScan={handleScanInvoice}
        showPromoInput
        promoPlaceholder="Enter promo code if you have one"
      />

      <Dialog open={Boolean(scanError)} onOpenChange={(open) => !open && setScanError('')}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="scan-error-modal">
          <DialogTitle className="text-lg font-bold">Scan Unavailable</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {scanError}
          </DialogDescription>
          <button
            type="button"
            onClick={() => setScanError('')}
            className="w-full h-10 rounded-xl gradient-brand-btn text-sm font-semibold"
            data-testid="scan-error-close-btn"
          >
            Close
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
